import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { URLConstants } from '@app/core/constants/URLConstants';
import moment from 'moment-timezone';
import { IMaramatakaDay, IMaramatakaWeek, IMaramatakaMonth } from '@app/data/interface/maramataka';
import { environment } from '@env/environment';
import { Observable, forkJoin, of } from 'rxjs';
import { APIWithLoaderHelper } from '../helpers/apiWithLoaderHelper';
import { mashMaramatakaDayData, mashMaramatakaWeekData, mashMaramatakaMonthData } from '@app/data/helpers/dataHelpers';
import { switchMap, take, catchError, map } from 'rxjs/operators';
import { LocationParamsService } from './locationParamsService';
import { LoaderService } from './loader.services';
import { FilterService } from './filterService';
import { getLocationQueryValue } from '../helpers/locationHelpers';

@Injectable({
  providedIn: 'root',
})
export class ChartService {
  constructor(
    private http: HttpClient,
    private apiWithLoaderHelper: APIWithLoaderHelper,
    private locationParamsService: LocationParamsService,
    private loaderService: LoaderService,
    private filterService: FilterService
  ) {}

  private getRongoReportingFilterParams(groupBy?: string | null): any {
    const filters = this.filterService.getFilters();
    const organisationSelection = filters.selectedContributingOrganisations || [];
    const projectSelection = filters.selectedContributingProjectListItems || [];
    const originOrganisationIds = organisationSelection.filter((id) => id !== 'all');
    const projectIds = projectSelection.filter((id) => id !== 'all');
    const params: any = {};

    if (Array.isArray(filters.selectedContributingOrganisations) && organisationSelection.length === 0) {
      // Explicit None: use an id that cannot match any observation.
      params.contributingOrganisationIds = '0';
    } else if (originOrganisationIds.length) {
      params.contributingOrganisationIds = originOrganisationIds.join(',');
    }

    if (Array.isArray(filters.selectedContributingProjectListItems) && projectSelection.length === 0) {
      params.contributingProjectIds = '0';
    } else if (projectIds.length) {
      params.contributingProjectIds = projectIds.join(',');
    }

    if (groupBy) {
      params.groupBy = groupBy;
    }

    return params;
  }

  private getSingleUrl = (url: string, params: any = {}) => {
    return new Observable<any>((observer) => {
      this.locationParamsService
        .getUrlWithParams(url, params)
        .pipe(take(1))
        .subscribe({
          next: (urlWithParams) =>
            this.apiWithLoaderHelper
              .get(urlWithParams)
              .pipe(take(1))
              .subscribe({
                next: (data: any) => observer.next(data),
                error: (err: any) => console.error(err),
                complete: () => observer.complete(),
              }),
        });
    });
  };

  public getMaramatakaDataDays(startDate: string, endDate: string, maramataka: any): Observable<IMaramatakaDay[]> {
    if (!maramataka) return new Observable<IMaramatakaDay[]>((observer) => observer.next([]));
    const utcStartDateTime = moment.tz(`${startDate} 00:00:00`, 'Pacific/Auckland').utc().toISOString();
    const utcEndDateTime = moment.tz(`${endDate} 23:59:59`, 'Pacific/Auckland').utc().toISOString();
    const filters = this.filterService.getFilters();
    return new Observable<IMaramatakaDay[]>((observer) => {
      forkJoin([
        this.getMaramatakaDays(utcStartDateTime, utcEndDateTime).pipe(take(1)),
        this.getMatauranga(utcStartDateTime, utcEndDateTime).pipe(take(1)),
        this.getAstronomicalAndMeteorologicalEvents(utcStartDateTime, utcEndDateTime).pipe(take(1)),
        this.getEnvironmentalDataForSiteByDate(startDate, endDate, 'hour').pipe(take(1)),
        this.getObservationData(startDate, endDate, filters.selectedLocations).pipe(take(1)),
      ]).subscribe({
        next: ([
          maramatakaDays,
          matauranga,
          astrologicalAndMeteorologicalEvents,
          environmentalData,
          observations,
        ]: any) => {
          const data = mashMaramatakaDayData(
            maramataka,
            maramatakaDays,
            matauranga,
            astrologicalAndMeteorologicalEvents,
            environmentalData,
            observations
          );
          observer.next(data);
        },
        error: (err: any) => console.error(err),
        complete: () => {
          this.loaderService.hide();
          observer.complete();
        },
      });
    });
  }

  public getMaramataka(): Observable<any> {
    return this.getSingleUrl(URLConstants.getMaramataka, {});
  }

  public getMaramatakaDays(startDateTime: string, endDateTime: string): Observable<any> {
    return this.getSingleUrl(URLConstants.getMaramatakaDays, { startDateTime, endDateTime });
  }

  public getMaramatakaWeeks(startDateTime: string, endDateTime: string): Observable<any> {
    return this.getSingleUrl(URLConstants.getMaramatakaWeeks, { startDateTime, endDateTime });
  }

  public getMaramatakaMonths(startDateTime: string, endDateTime: string): Observable<any> {
    return this.getSingleUrl(URLConstants.getMaramatakaMonths, { startDateTime, endDateTime });
  }

  public getMaramatakaStars(startDateTime: string, endDateTime: string): Observable<any> {
    return this.getSingleUrl(URLConstants.getMaramatakaStars, { startDateTime, endDateTime });
  }

  public getMaramatakaMonthWeeks(startDateTime: string, endDateTime: string): Observable<any> {
    return this.getSingleUrl(URLConstants.getMaramatakaMonthWeeks, { startDateTime, endDateTime });
  }

  public getMatauranga(startDateTime: string, endDateTime: string): Observable<any> {
    return this.getSingleUrl(URLConstants.getMaramatakaMatauranga, { startDateTime, endDateTime });
  }

  public getAstronomicalAndMeteorologicalEvents(startDateTime: string, endDateTime: string): Observable<any> {
    return this.getSingleUrl(URLConstants.getMaramatakaSiteAstronomicalMeteorologicalEvents, {
      startDateTime,
      endDateTime,
    });
  }

  public getWeatherData(startDate: string, endDate: string): Observable<any> {
    return this.getSingleUrl(URLConstants.getHistoricalAndForecastedData, { startDate, endDate });
  }

  public getMauriRoseData(
    startDate: string,
    endDate: string,
    locationIds: any,
    groupBy?: string | null
  ): Observable<any> {
    return this.getSingleUrl(URLConstants.getMauriObservations, {
      startDate,
      endDate,
      locationId: getLocationQueryValue(locationIds),
      ...this.getRongoReportingFilterParams(groupBy),
    });
  }

  public getWaiQualityData(
    startDate: string,
    endDate: string,
    locationIds: any,
    groupBy?: string | null
  ): Observable<any> {
    return this.getSingleUrl(URLConstants.getWaterQualityObservations, {
      startDate,
      endDate,
      locationId: getLocationQueryValue(locationIds),
      ...this.getRongoReportingFilterParams(groupBy),
    });
  }

  public getPestCountData(
    startDate: string,
    endDate: string,
    locationIds: any,
    groupBy?: string | null
  ): Observable<any> {
    return this.getSingleUrl(URLConstants.getPestCountReport, {
      startDate,
      endDate,
      locationId: getLocationQueryValue(locationIds),
      ...this.getRongoReportingFilterParams(groupBy),
    });
  }

  public getHonongaData(startDate: string, endDate: string, groupBy?: string | null): Observable<any> {
    return this.getSingleUrl(URLConstants.getHonongaReport, {
      startDate,
      endDate,
      ...this.getRongoReportingFilterParams(groupBy),
    });
  }

  public getKaupapaData(
    startDate: string,
    endDate: string,
    locationIds: any,
    groupBy?: string | null
  ): Observable<any> {
    return this.getSingleUrl(URLConstants.getKaupapaReport, {
      startDate,
      endDate,
      locationId: getLocationQueryValue(locationIds),
      ...this.getRongoReportingFilterParams(groupBy),
    });
  }

  public getTaongaAbundanceData(
    startDate: string,
    endDate: string,
    locationIds: any,
    groupBy?: string | null
  ): Observable<any> {
    return this.getSingleUrl(URLConstants.getTaongaAbundanceReport, {
      startDate,
      endDate,
      locationId: getLocationQueryValue(locationIds),
      ...this.getRongoReportingFilterParams(groupBy),
    });
  }

  public getWaiMauriData(
    startDate: string,
    endDate: string,
    locationIds: any,
    groupBy?: string | null
  ): Observable<any> {
    return this.getSingleUrl(URLConstants.getWaiMauriReport, {
      startDate,
      endDate,
      locationId: getLocationQueryValue(locationIds),
      ...this.getRongoReportingFilterParams(groupBy),
    });
  }

  public getMauriData(startDate: string, endDate: string, locationIds: any, groupBy?: string | null): Observable<any> {
    return this.getSingleUrl(URLConstants.getMauriReport, {
      startDate,
      endDate,
      locationId: getLocationQueryValue(locationIds),
      ...this.getRongoReportingFilterParams(groupBy),
    });
  }

  public getRiparianFeaturesData(
    startDate: string,
    endDate: string,
    locationIds: any,
    groupBy?: string | null
  ): Observable<any> {
    return this.getSingleUrl(URLConstants.getRiparianFeaturesReport, {
      startDate,
      endDate,
      locationId: getLocationQueryValue(locationIds),
      ...this.getRongoReportingFilterParams(groupBy),
    });
  }

  public getMarmatakaDataWeek(startDate: string, endDate: string, maramataka: any): Observable<IMaramatakaWeek[]> {
    if (!maramataka) return new Observable<IMaramatakaWeek[]>((observer) => observer.next([]));
    const utcStartDateTime = moment.tz(`${startDate} 00:00:00`, 'Pacific/Auckland').utc().toISOString();
    const utcEndDateTime = moment.tz(`${endDate} 23:59:59`, 'Pacific/Auckland').utc().toISOString();
    const filters = this.filterService.getFilters();
    return new Observable<IMaramatakaWeek[]>((observer) => {
      forkJoin([
        this.getMaramatakaWeeks(utcStartDateTime, utcEndDateTime).pipe(take(1)),
        this.getMatauranga(utcStartDateTime, utcEndDateTime).pipe(take(1)),
        this.getEnvironmentalDataForSiteByDate(startDate, endDate, 'day').pipe(take(1)),
        this.getObservationData(startDate, endDate, filters.selectedLocations).pipe(take(1)),
        this.getWeatherData(startDate, endDate).pipe(take(1)),
      ]).subscribe({
        next: ([maramatakaDays, matauranga, environmentalData, observations, weather]: any) => {
          const data = mashMaramatakaWeekData(
            maramataka,
            maramatakaDays,
            matauranga,
            environmentalData,
            observations,
            weather
          );
          observer.next(data);
        },
        error: (err: any) => console.error(err),
        complete: () => {
          this.loaderService.hide();
          observer.complete();
        },
      });
    });
  }

  public getMarmatakaDataMonth(startDate: string, endDate: string, maramataka: any): Observable<IMaramatakaMonth[]> {
    if (!maramataka) return new Observable<IMaramatakaMonth[]>((observer) => observer.next([]));
    const utcStartDateTime = moment.tz(`${startDate} 00:00:00`, 'Pacific/Auckland').utc().toISOString();
    const utcEndDateTime = moment.tz(`${endDate} 23:59:59`, 'Pacific/Auckland').utc().toISOString();
    const filters = this.filterService.getFilters();
    return new Observable<IMaramatakaMonth[]>((observer) => {
      forkJoin([
        this.getMaramatakaMonths(utcStartDateTime, utcEndDateTime).pipe(take(1)),
        this.getMaramatakaStars(utcStartDateTime, utcEndDateTime).pipe(take(1)),
        this.getMaramatakaMonthWeeks(utcStartDateTime, utcEndDateTime).pipe(take(1)),
        this.getMatauranga(utcStartDateTime, utcEndDateTime).pipe(take(1)),
        this.getEnvironmentalDataForSiteByDate(startDate, endDate, 'day').pipe(take(1)),
        this.getObservationData(startDate, endDate, filters.selectedLocations).pipe(take(1)),
      ]).subscribe({
        next: ([
          maramatakaDays,
          maramatakaStars,
          maramatakaMonthWeeks,
          matauranga,
          environmentalData,
          observations,
        ]: any) => {
          const data = mashMaramatakaMonthData(
            maramataka,
            maramatakaDays,
            maramatakaStars,
            maramatakaMonthWeeks,
            matauranga,
            environmentalData,
            observations
          );
          observer.next(data);
        },
        error: (err: any) => console.error(err),
        complete: () => {
          this.loaderService.hide();
          observer.complete();
        },
      });
    });
  }

  // TECHNICAL-DEBT
  public getMarmataEventData(events: any[]): Observable<{ data: any }> {
    return this.locationParamsService.getUrlWithParams(URLConstants.getMaramatakaEventData).pipe(
      switchMap((urlWithParams) => {
        return this.apiWithLoaderHelper.post<{ data: any }>(urlWithParams, { events }, {}, false);
      })
    );
  }

  /**
   * Get environmental data for all sources for the current site
   * Wrap the call so that HTTP errors (e.g. 404 for an environmental source)
   * don't cause forkJoin to error out. Instead log a warning and return null.
   *
   * This will allow widgets that utilize this data to still load even if
   * one or more environmental data sources are not available.
   *
   * @param startDate The start date (YYYY-MM-DD)
   * @param endDate The end date (YYYY-MM-DD)
   * @param granularity The granularity of the data to retrieve from the backend service (e.g. hour, day)
   * @param basicResults  Whether to return basic results only (true) or full results (false)
   * @returns
   */
  /**
   * Narrow the site-by-date environmental response to the selected monitoring
   * locations and monitoring groups. Tūhono loads all sources and filters the
   * response client-side so both filters can be combined without a new API
   * contract.
   */
  private filterEnvironmentalDataBySelection(response: any): any {
    if (!response) return response;

    const filters = this.filterService.getFilters();
    const sourceSelection = Array.isArray(filters.selectedEnvironmentalDataSources)
      ? filters.selectedEnvironmentalDataSources
      : ['all'];
    const groupSelection = Array.isArray(filters.selectedEnvironmentalDataSourceDashGroups)
      ? filters.selectedEnvironmentalDataSourceDashGroups
      : ['all'];

    const noSources = sourceSelection.length === 0;
    const noGroups = groupSelection.length === 0;
    if (noSources || noGroups) {
      return { ...response, data: [], environmentalDataSources: [] };
    }

    const sourceIds = sourceSelection.includes('all') ? null : new Set(sourceSelection.map(String));
    const groupIds = groupSelection.includes('all') ? null : new Set(groupSelection);
    const sources = Array.isArray(response.environmentalDataSources) ? response.environmentalDataSources : [];

    const allowedSources = sources.filter((source: any) => {
      if (sourceIds && !sourceIds.has(String(source.id))) return false;
      if (groupIds && !(source.dash_groupings || []).some((group: string) => groupIds.has(group))) return false;
      return true;
    });
    const allowedIds = new Set(allowedSources.map((source: any) => String(source.id)));

    return {
      ...response,
      environmentalDataSources: allowedSources,
      data: (response.data || []).filter((row: any) => allowedIds.has(String(row.environmental_data_source_id))),
    };
  }

  public getEnvironmentalDataForSiteByDate(
    startDate: string,
    endDate: string,
    granularity?: string,
    basicResults: boolean = true
  ): Observable<any> {
    const params = {
      environmental_data_source_id: 'all',
      analyte_id: 'all',
      granularity_override: granularity,
      startDate,
      endDate,
      basic_results: basicResults ? 'true' : 'false',
    };

    return this.locationParamsService.getUrlWithParams(URLConstants.getEnvironmentalDataForSiteByDate, params).pipe(
      take(1),
      switchMap((urlWithParams) =>
        this.apiWithLoaderHelper.get(urlWithParams).pipe(
          take(1),
          map((response: any) => this.filterEnvironmentalDataBySelection(response)),
          catchError((err: any) => {
            console.warn(
              `[ChartService] Environmental data load failed for site by date (${startDate} - ${endDate}):`,
              err && err.status ? `status ${err.status}` : err
            );
            return of(null);
          })
        )
      )
    );
  }

  /**
   * Get environmental data for a specific source for the current site
   * Wrap the call so that HTTP errors (e.g. 404 for an environmental source)
   * don't cause forkJoin to error out. Instead log a warning and return null.
   *
   * This will allow widgets that utilize this data to still load even if
   * one or more environmental data sources are not available.
   *
   * @param environmentalDataSourceIdentifier The environmental data source identifier
   * @param startDate The start date (YYYY-MM-DD)
   * @param endDate The end date (YYYY-MM-DD)
   * @param granularity The granularity of the data to retrieve from the backend service (e.g. hour, day)
   * @param basicResults Whether to return basic results only (true) or full results (false)
   * @returns
   */
  public getEnvironmentalDataForSourceByDate(
    environmentalDataSourceIdentifier: string,
    startDate: string,
    endDate: string,
    granularity?: string,
    basicResults: boolean = true
  ): Observable<any> {
    const params = {
      environmental_data_source_id: encodeURIComponent(environmentalDataSourceIdentifier),
      analyte_id: 'all',
      granularity_override: granularity,
      startDate,
      endDate,
      basic_results: basicResults ? 'true' : 'false',
    };

    return this.locationParamsService.getUrlWithParams(URLConstants.getEnvironmentalDataForSiteByDate, params).pipe(
      take(1),
      switchMap((urlWithParams) =>
        this.apiWithLoaderHelper.get(urlWithParams).pipe(
          take(1),
          catchError((err: any) => {
            console.warn(
              `[ChartService] Environmental data load failed for source "${environmentalDataSourceIdentifier}" (${startDate} - ${endDate}):`,
              err && err.status ? `status ${err.status}` : err
            );
            return of(null);
          })
        )
      )
    );
  }

  /**
   * Get environmental data for a specific source and analyte for the current site
   * Wrap the call so that HTTP errors (e.g. 404 for an environmental source)
   * don't cause forkJoin to error out. Instead log a warning and return null.
   *
   * This will allow widgets that utilize this data to still load even if
   * one or more environmental data sources are not available.
   *
   * If entityNames are provided, they will be sent in the POST body,
   * otherwise a GET request will be made.
   *
   * @param environmentalDataSourceIdentifier The environmental data source identifier
   * @param analyteIdentifier The analyte identifier
   * @param startDate The start date (YYYY-MM-DD)
   * @param endDate The end date (YYYY-MM-DD)
   * @param granularity The granularity of the data to retrieve from the backend service (e.g. hour, day)
   * @returns
   */
  public getEnvironmentalDataForSourceAndAnalyteByDate(
    environmentalDataSourceIdentifier: string,
    analyteIdentifier: string,
    startDate: string,
    endDate: string,
    granularity?: string,
    entityNames?: string[]
  ): Observable<any> {
    console.debug(
      `[ChartService] Requesting environmental data for source="${environmentalDataSourceIdentifier}" analyte="${analyteIdentifier}" start="${startDate}" end="${endDate}" granularity="${granularity}" entityNamesCount=${
        entityNames?.length ?? 0
      }`
    );
    const params = {
      environmental_data_source_id: encodeURIComponent(environmentalDataSourceIdentifier),
      analyte_id: analyteIdentifier,
      granularity_override: granularity,
      startDate,
      endDate,
    };

    if (entityNames && entityNames.length) {
      const postBody = { entity_names: entityNames };
      return this.locationParamsService
        .postUrlWithParams(URLConstants.getEnvironmentalDataForSiteByDate, params, postBody)
        .pipe(
          take(1),
          switchMap(({ url, body }) => {
            console.debug('[ChartService] POST URL:', url);
            console.debug('[ChartService] POST body keys:', Object.keys(body || {}));
            if (body && Array.isArray((body as any).entity_names)) {
              console.debug('[ChartService] entity_names count:', (body as any).entity_names.length);
            }

            return this.apiWithLoaderHelper.post(url, body, {}, false).pipe(
              take(1),
              catchError((err: any) => {
                console.warn(
                  `[ChartService] Environmental data load failed for source "${environmentalDataSourceIdentifier}" analyte "${analyteIdentifier}" (${startDate} - ${endDate}):`,
                  err && err.status ? `status ${err.status}` : err
                );
                return of(null);
              })
            );
          })
        );
    }

    return this.locationParamsService.getUrlWithParams(URLConstants.getEnvironmentalDataForSiteByDate, params).pipe(
      take(1),
      switchMap((urlWithParams) =>
        this.apiWithLoaderHelper.get(urlWithParams).pipe(
          take(1),
          catchError((err: any) => {
            console.warn(
              `[ChartService] Environmental data load failed for source "${environmentalDataSourceIdentifier}" analyte "${analyteIdentifier}" (${startDate} - ${endDate}):`,
              err && err.status ? `status ${err.status}` : err
            );
            return of(null);
          })
        )
      )
    );
  }

  // TECHNICAL-DEBT  (still used by filter-drawer-layout-component)
  public getSurfaceWaterData(startDate: string, endDate: string): Observable<any> {
    return this.locationParamsService.getUrlWithParams(URLConstants.getSurfaceWaterData).pipe(
      switchMap((urlWithParams) => {
        return this.apiWithLoaderHelper.post<any>(urlWithParams, { startDate, endDate }, {}, false);
      })
    );
  }

  // TECHNICAL-DEBT (still used by filter-drawer-layout-component)
  public getGroundWaterData(startDate: string, endDate: string): Observable<any> {
    return this.locationParamsService.getUrlWithParams(URLConstants.getGroundWaterData).pipe(
      switchMap((urlWithParams) => {
        return this.apiWithLoaderHelper.post<any>(urlWithParams, { startDate, endDate }, {}, false);
      })
    );
  }

  public getSingleObservationData(observationId: string): Observable<any> {
    return this.getSingleUrl(URLConstants.getObservation, { observation_id: observationId });
  }

  public getObservationData(startDate: string, endDate: string, locationIds: any): Observable<any> {
    return this.getSingleUrl(URLConstants.getSiteObservations, {
      startDate,
      endDate,
      selectedLocationId: getLocationQueryValue(locationIds),
      ...this.getRongoReportingFilterParams(),
    });
  }

  public getObservationSummaries(
    startDate: string,
    endDate: string,
    locationIds: any,
    searchTerm: string
  ): Observable<any> {
    return this.getSingleUrl(URLConstants.getSiteObservationsSummary, {
      startDate,
      endDate,
      selectedLocationId: getLocationQueryValue(locationIds),
      searchTerm,
    });
  }

  public getObservationCsv(startDate: string, endDate: string, locationIds: any, searchTerm: string): Observable<any> {
    const params: any = {
      startDate,
      endDate,
      searchTerm,
    };

    const locationQueryValue = getLocationQueryValue(locationIds);
    if (locationQueryValue) {
      params.selectedLocationId = locationQueryValue;
    }

    return this.getSingleUrl(URLConstants.getObservationCsv, params);
  }

  // TECHNICAL-DEBT
  public getWeeklyOpenChartData(startDate: any): Observable<any> {
    // start date is the unix of passed param, or hardcoded for 20/05/2022
    const unixStartDate = startDate ? new Date(`${startDate} 00:00:00`).getTime() / 1000 : 1652976000;

    // end date is unix of current date
    const unixEndDate = Math.floor(Date.now() / 1000);

    return this.http.get(
      `https://history.openweathermap.org/data/2.5/history/city?lat=33.44&lon=-94.04&type=hour&start=${unixStartDate}&end=${unixEndDate}&appid=${environment.weatherAPI}`
    );
  }

  // TECHNICAL-DEBT
  public getLineChartData(): Observable<any> {
    return this.http.get(`${environment.apiBaseURL + URLConstants.getLineChartData}`);
  }

  public getMarmatakaRecentEvents(startDate: string, endDate: string): Observable<any> {
    return this.getSingleUrl(URLConstants.getMarmatakaEvents, { startDate, endDate });
  }

  public getHistoricalWeatherData(startDate: string, endDate: string): Observable<any> {
    return this.getSingleUrl(URLConstants.getHistoricalWeather, { start: startDate, end: endDate });
  }

  public getForecastedWeatherData(): Observable<any> {
    return this.getSingleUrl(URLConstants.getForecastedWeather, {});
  }

  // TECHNICAL-DEBT
  public getMarmatakaMoonPhases(startDate: string, endDate: string): Observable<any> {
    return this.locationParamsService.getUrlWithParams(URLConstants.getMarmatakaMoonPhases).pipe(
      switchMap((urlWithParams) => {
        return this.apiWithLoaderHelper.post<any>(urlWithParams, { startDate, endDate }, {}, false);
      })
    );
  }

  // Admin data management endpoints
  public getAllEnvironmentalDataSources(): Observable<any> {
    return this.getSingleUrl('/environmental-data/all/sources', {});
  }

  // Data Availability for admin data management
  public getDataAvailability(startDate: string, endDate: string): Observable<any> {
    return new Observable<any>((observer) => {
      this.locationParamsService
        .getUrlWithParams('/environmental-data/data-availability', { startDate, endDate })
        .pipe(take(1))
        .subscribe({
          next: (urlWithParams: string) =>
            this.apiWithLoaderHelper
              .get(urlWithParams)
              .pipe(take(1))
              .subscribe({
                next: (data: any) => observer.next(data),
                error: (err: any) => console.error(err),
                complete: () => observer.complete(),
              }),
        });
    });
  }

  // CSV Upload method for data management
  public uploadCsv(file: File): Observable<any> {
    return new Observable<any>((observer) => {
      this.locationParamsService
        .getUrlWithParams('/environmental-data/bulk-upload', {})
        .pipe(take(1))
        .subscribe({
          next: (urlWithParams) =>
            file.text().then((csv) =>
              this.apiWithLoaderHelper
                .post<{ data: any }>(urlWithParams, csv, {}, false, { 'Content-Type': 'text/csv' })
                .pipe(take(1))
                .subscribe({
                  next: (data: any) => observer.next(data),
                  error: (err: any) => console.error(err),
                  complete: () => observer.complete(),
                })
            ),
        });
    });
  }

  // Create new environmental data source
  public createEnvironmentalDataSource(data: any): Observable<any> {
    return new Observable<any>((observer) => {
      this.locationParamsService
        .getUrlWithParams('/environmental-data/sources', {})
        .pipe(take(1))
        .subscribe({
          next: (urlWithParams) =>
            this.apiWithLoaderHelper
              .post<{ data: any }>(urlWithParams, data, {}, false)
              .pipe(take(1))
              .subscribe({
                next: (data: any) => observer.next(data),
                error: (err: any) => observer.error(err),
                complete: () => observer.complete(),
              }),
        });
    });
  }

  // Update existing environmental data source
  public updateEnvironmentalDataSource(id: string, data: any): Observable<any> {
    return new Observable<any>((observer) => {
      this.locationParamsService
        .getUrlWithParams(`/environmental-data/sources/${id}`, {})
        .pipe(take(1))
        .subscribe({
          next: (urlWithParams) =>
            this.apiWithLoaderHelper
              .post<{ data: any }>(urlWithParams, data, {}, false)
              .pipe(take(1))
              .subscribe({
                next: (data: any) => observer.next(data),
                error: (err: any) => observer.error(err),
                complete: () => observer.complete(),
              }),
        });
    });
  }

  // Get all species entities
  public getAllSpecies(): Observable<any> {
    return this.getSingleUrl('/rongo/entities', {});
  }
}
